import { useNavigate } from 'react-router';
import { Alert, StatusType } from '@platform/witems';
import {
  spruceInboundOf, type SpruceInbound, type SpruceInboundKind, type SpruceTaskLike,
} from '../logic/spruceInbound';
import { Constants } from '../config/constants';

type InboundTask = SpruceTaskLike & { withings_user_id?: string | number | null };

const KIND_LABEL: Record<SpruceInboundKind, string> = {
  sms: 'Text',
  voicemail: 'Voicemail',
  missed_call: 'Missed call',
  shared_phone: 'Message',
};

function receivedWhen(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (at.toDateString() === new Date().toDateString()) return time;
  return `${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
}

function InboundAlert({ inbound, withingsUserId }: { inbound: SpruceInbound; withingsUserId: string | null }) {
  const navigate = useNavigate();
  const when = receivedWhen(inbound.receivedAt);
  const from = inbound.kind === 'shared_phone' ? ' from a number two patients share' : '';
  const text = `${KIND_LABEL[inbound.kind]} received ${when ? `${when} ` : ''}via the clinic line${from} · sender unverified`;
  const link = inbound.threadUrl
    ? { linkText: 'Open in Spruce', linkURL: inbound.threadUrl, linkInNewTab: true }
    : {};
  return (
    <div style={{
      margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: 8,
    }}
    >
      <Alert type={StatusType.MODERATE} text={text} {...link} />
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}
      >
        {withingsUserId && (
          // Not the witems Button: its MINI size drops the label padding (docs/witems-feedback.md).
          <button
            type="button"
            className="task-action-btn"
            onClick={(e) => { e.stopPropagation(); navigate(`/engage/${encodeURIComponent(withingsUserId)}`); }}
          >
            Reply in chat
          </button>
        )}
        <span className="detail1 text-secondary">
          Closes on its own at the next hourly check after you reply in the chat or call the patient.
        </span>
      </div>
    </div>
  );
}

// Mounted on BOTH task surfaces (drawer + patient-file card), so they never disagree about a clinic-line task.
export default function SpruceInboundStatus({ task }: { task: InboundTask | null | undefined }) {
  const inbound = spruceInboundOf(task, Constants.SPRUCE_ORG_ID);
  if (!inbound) return null;
  const uid = task?.withings_user_id != null && String(task.withings_user_id) !== '' ? String(task.withings_user_id) : null;
  return <InboundAlert inbound={inbound} withingsUserId={uid} />;
}
